import { z } from 'zod';
import { ACCOUNT_TYPES, ACTIONS, APP_CATEGORIES, BROWSERS, DECISIONS, OUTCOMES, SENSITIVITY_LEVELS, SEVERITIES } from '../types.js';
import { THREAT_CATEGORIES } from '../threat/types.js';
import { EXTENSION_ID_RE } from '../extensions/types.js';

/**
 * Wire contract between extension and cloud. PRIVACY BY CONSTRUCTION: there is no
 * field in which page content, prompt text, clipboard text, file contents, cookies,
 * tokens or passwords could be sent. Findings are counts per detector. `.strict()`
 * makes the server reject any unknown field rather than silently storing it.
 */

const shortId = z.string().min(1).max(64).regex(/^[A-Za-z0-9_.:-]+$/);
/** Canonical hostnames (net/hostname.ts): LDH labels plus `_`, IPv4, or a bracketed IPv6 literal. */
const hostname = z.string().min(1).max(253).regex(/^(?:[a-z0-9_.-]+|\[[0-9a-f:.]+\])$/);

const EVENT_TYPES = [
  'app.visit',
  'data.paste',
  'data.upload',
  'data.download',
  'data.submit',
  'web.navigate',
  'ai.prompt',
  // Phase 4 web threat protection (sent only by extensions holding a protection bundle, i.e. to servers that serve it).
  'web.threat_blocked',
  'web.threat_warned',
  'web.threat_monitored',
  // Phase 5 download threat protection (same condition).
  'download.threat_blocked',
  'download.threat_warned',
  /** The reputation lookup failed or timed out; the download was allowed (never silently blocked). */
  'download.lookup_failed',
] as const;

/**
 * Threat verdict attached to protection events (Phases 4–5). The category and a short reason
 * code only — never the URL (the hostname is in `app.hostname`).
 */
export const threatInfoSchema = z
  .object({
    category: z.enum(THREAT_CATEGORIES).nullable(),
    /** protection_list: the device's signed protection bundle; reputation_lookup: POST /ext/v1/reputation; download_policy: executable/reputable-site rule. */
    source: z.enum(['protection_list', 'reputation_lookup', 'download_policy']),
    /** e.g. domain_match, url_match, referrer_match, final_url_match, unknown_executable, lookup_failed. */
    reason: z.string().min(1).max(64).regex(/^[a-z0-9_]+$/),
  })
  .strict();
export type ThreatInfo = z.infer<typeof threatInfoSchema>;

export const telemetryEventSchema = z
  .object({
    /** Client-generated UUID; used for idempotent ingestion. */
    id: z.string().uuid(),
    type: z.enum(EVENT_TYPES),
    occurredAt: z.string().datetime(),
    action: z.enum(ACTIONS),
    decision: z.enum(DECISIONS),
    outcome: z.enum(OUTCOMES),
    severity: z.enum(SEVERITIES).nullable(),
    app: z
      .object({
        hostname,
        appId: shortId.nullable(),
        catalogKey: z.string().max(64).nullable(),
        category: z.enum(APP_CATEGORIES),
        suspectedAi: z.boolean().default(false),
      })
      .strict(),
    account: z
      .object({
        type: z.enum(ACCOUNT_TYPES),
        provider: z.string().max(40),
        domain: hostname.nullable(),
        identifier: z.string().max(254).nullable(),
      })
      .strict(),
    data: z
      .object({
        classifications: z.array(shortId).max(50),
        detectors: z.array(z.object({ id: shortId, count: z.number().int().min(0).max(1_000_000) }).strict()).max(100),
        maxSensitivity: z.enum(SENSITIVITY_LEVELS).nullable(),
        truncated: z.boolean(),
        /** Where content came from, e.g. "clipboard", "file", "prompt", "form" — never the content. */
        source: z.enum(['clipboard', 'file', 'prompt', 'form', 'drop']),
      })
      .strict()
      .nullable(),
    file: z
      .object({
        name: z.string().max(255).nullable(),
        extension: z.string().max(16),
        mime: z.string().max(127),
        sizeBytes: z.number().int().min(0).max(1e13),
        inspected: z.boolean(),
      })
      .strict()
      .nullable(),
    policy: z
      .object({
        ruleId: shortId.nullable(),
        version: z.number().int().min(0),
        matchedRuleIds: z.array(shortId).max(50),
      })
      .strict(),
    /** Business justification entered by the user for a JUSTIFY decision. Scanned locally first; redacted if sensitive. */
    justification: z.string().max(500).nullable(),
    /** Local inspection cost, for performance monitoring. */
    inspectMs: z.number().min(0).max(600_000).nullable(),
    /** Optional: present on web/download threat events only (see threatInfoSchema). */
    threat: threatInfoSchema.nullable().optional(),
  })
  .strict();
export type TelemetryEvent = z.infer<typeof telemetryEventSchema>;

export const MAX_EVENTS_PER_BATCH = 200;
export const MAX_TELEMETRY_BODY_BYTES = 512 * 1024;

export const telemetryBatchSchema = z
  .object({
    batchId: z.string().uuid(),
    sentAt: z.string().datetime(),
    events: z.array(telemetryEventSchema).min(1).max(MAX_EVENTS_PER_BATCH),
    /** Events the device had to discard because its local queue was full. */
    droppedEvents: z.number().int().min(0).max(1e9).default(0),
  })
  .strict();

const deviceInfoSchema = z
  .object({
    os: z.enum(['mac', 'win', 'linux', 'cros', 'android', 'openbsd', 'fuchsia', 'unknown']),
    arch: z.string().max(20),
    browser: z.enum(BROWSERS),
    browserVersion: z.string().max(40),
    extensionVersion: z.string().regex(/^\d+\.\d+\.\d+$/),
    /** True when configuration arrived via enterprise managed storage (MDM / GPO). */
    managed: z.boolean(),
    /** Optional label supplied by MDM managed configuration. */
    deviceLabel: z.string().max(100).nullable(),
    /** Browser profile email (chrome.identity.getProfileUserInfo); reported, not proof of identity. */
    profileEmail: z.string().max(254).nullable(),
  })
  .strict();
export type DeviceInfo = z.infer<typeof deviceInfoSchema>;

export const enrollRequestSchema = z
  .object({
    enrollmentToken: z.string().min(20).max(200),
    installId: z.string().uuid(),
    device: deviceInfoSchema,
  })
  .strict();

export const tokenRequestSchema = z
  .object({ deviceId: z.string().uuid(), deviceSecret: z.string().min(20).max(200) })
  .strict();

const extensionInventoryItemSchema = z
  .object({
    id: z.string().regex(/^[a-p]{32}$|^[A-Za-z0-9_.@{}-]{1,80}$/),
    name: z.string().max(200),
    version: z.string().max(40),
    enabled: z.boolean(),
    installType: z.enum(['admin', 'development', 'normal', 'sideload', 'other']),
    permissions: z.array(z.string().max(100)).max(100),
    hostPermissions: z.array(z.string().max(300)).max(200),
    homepageUrl: z.string().max(500).nullable(),
    updateUrl: z.string().max(500).nullable(),
    mayDisable: z.boolean(),
  })
  .strict();
export type ExtensionInventoryItem = z.infer<typeof extensionInventoryItemSchema>;

/**
 * Protection bundle health (Phases 3–6). Sent only by extensions that understand the protection
 * bundle, and only to servers that serve it (GET /ext/v1/protection answered), so heartbeats to
 * older servers — whose schema is strict — never carry it. Counts and extension ids only: no URLs.
 */
const protectionHealthSchema = z
  .object({
    /** Active (verified) protection bundle version, or null when none is active. */
    version: z.number().int().min(0).nullable(),
    lastResult: z.enum(['activated', 'unchanged', 'rejected', 'error', 'unsupported']).nullable(),
    lastError: z.string().max(200).nullable(),
    /** Web protection: domains in the bundle, domains installed as network rules, domains left out (browser limits). */
    webDomains: z.number().int().min(0).max(1e7),
    webInstalledDomains: z.number().int().min(0).max(1e7),
    webTruncatedDomains: z.number().int().min(0).max(1e7),
    /**
     * Privacy: tracker domains whose third-party requests are blocked by installed rules / left out
     * (browser limits), and query parameters stripped from navigations. Configuration counts: the
     * number of blocked requests is not observable without declarativeNetRequestFeedback (not requested).
     */
    trackerDomainsBlocked: z.number().int().min(0).max(1e7),
    trackerTruncatedDomains: z.number().int().min(0).max(1e7),
    stripParams: z.number().int().min(0).max(1000),
    /** Extensions from `extensions.disableIds` that Ward has disabled / could not disable (policy-installed, error). */
    disabledByWard: z.array(z.string().regex(EXTENSION_ID_RE)).max(1000),
    couldNotDisable: z.array(z.string().regex(EXTENSION_ID_RE)).max(1000),
  })
  .strict();
export type ProtectionHealth = z.infer<typeof protectionHealthSchema>;

export const heartbeatSchema = z
  .object({
    sentAt: z.string().datetime(),
    device: deviceInfoSchema,
    policyVersion: z.number().int().min(0).nullable(),
    policyActivatedAt: z.string().datetime().nullable(),
    policyStale: z.boolean(),
    queueDepth: z.number().int().min(0).max(1e9),
    droppedEvents: z.number().int().min(0).max(1e9),
    grantedPermissions: z.array(z.string().max(100)).max(50),
    grantedOrigins: z.array(z.string().max(300)).max(50),
    lastError: z.string().max(300).nullable(),
    /** Present only when the inventory changed since the last report (hash differs). */
    extensions: z.array(extensionInventoryItemSchema).max(500).nullable(),
    extensionsHash: z.string().max(128).nullable(),
    /** Optional (newer extensions talking to newer servers only): see protectionHealthSchema. */
    protection: protectionHealthSchema.optional(),
  })
  .strict();
export type Heartbeat = z.infer<typeof heartbeatSchema>;
